import React, { memo, useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Pagination,
  ScrollArea,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { toast } from "react-toastify";
import { Contact } from "../../interfaces/services/Content";
import contentService from "../../services/api/contentService";
import { FaEnvelope, FaEye, FaPhone } from "react-icons/fa";
import { BookCheck } from "lucide-react";

type ContactResp = {
  pageSize: number;
  pageIndex: number;
  pageCount: number;
  items: Contact[];
  totalCount: number;
};

const ContactRow = memo(function ContactRow({
  item,
  onOpen,
  onCopyEmail,
  onCopyPhone,
  markAsRead,
}: {
  item: Contact;
  onOpen: (c: Contact) => void;
  onCopyEmail: (email: string) => void;
  onCopyPhone: (phone: string) => void;
  markAsRead: (code: string) => void;
}) {
  return (
    <Table.Tr key={item.code} className="hover:bg-gray-50 transition-colors">
      <Table.Td width={150}>
        {item.read ? (
          <Badge color="green" variant="light">
            Okundu
          </Badge>
        ) : (
          <div className="flex items-center gap-2">
            <Tooltip label="Okundu İşaretle">
              <ActionIcon variant="light" onClick={() => markAsRead(item.code)}>
                <BookCheck size={16} />
              </ActionIcon>
            </Tooltip>
            <Badge color="red" variant="light">
              Okunmadı
            </Badge>
          </div>
        )}
      </Table.Td>
      <Table.Td>
        <Text fw={600}>{item.fullName}</Text>
        <Group gap="xs" wrap="wrap">
          <a
            href={`mailto:${item.email}`}
            className="text-blue-600 hover:underline"
          >
            {item.email}
          </a>
          <Text c="dimmed">|</Text>
          <a
            href={`tel:${item.phone}`}
            className="text-blue-600 hover:underline"
          >
            {item.phone}
          </a>
        </Group>
      </Table.Td>
      <Table.Td>
        <Text c="dimmed" size="sm" lineClamp={1}>
          {item.subject ?? "—"}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" lineClamp={2}>
          {item.message}
        </Text>
      </Table.Td>
      <Table.Td width={160}>
        <Group gap="xs" justify="flex-end" wrap="nowrap">
          <Tooltip label="Detayı aç">
            <ActionIcon
              variant="subtle"
              onClick={() => onOpen(item)}
              aria-label="Detayı aç"
            >
              <FaEye />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="E-posta kopyala">
            <ActionIcon
              variant="subtle"
              onClick={() => onCopyEmail(item.email)}
              aria-label="E-posta kopyala"
            >
              <FaEnvelope />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Telefonu kopyala">
            <ActionIcon
              variant="subtle"
              onClick={() => onCopyPhone(item.phone)}
              aria-label="Telefonu kopyala"
            >
              <FaPhone />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
});

const ContactMessages: React.FC = () => {
  const [resp, setResp] = useState<ContactResp | null>(null);
  const [pageIndex, setPageIndex] = useState(0); // 0-based
  const [loading, setLoading] = useState(false);

  const [detailOpened, { open: openDetail, close: closeDetail }] =
    useDisclosure(false);
  const [selected, setSelected] = useState<Contact | null>(null);

  const getMessages = useCallback(async (index: number) => {
    setLoading(true);
    try {
      const r = (await contentService.getPagedContact(index)) as ContactResp;
      setResp(r);
    } catch (err) {
      console.error(err);
      toast.error("Mesajlar getirilirken hata oluştu", {
        toastId: "get-error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = useCallback(
    async (code: string) => {
      setLoading(true);
      try {
        await contentService.readContact(code);
        getMessages(pageIndex);
      } catch (err) {
        console.error(err);
        toast.error("Mesaj okunurken hata oluştu", {
          toastId: "read-error",
        });
      } finally {
        setLoading(false);
      }
    },
    [getMessages, pageIndex]
  );

  useEffect(() => {
    getMessages(pageIndex);
  }, [getMessages, pageIndex]);

  const openDetails = useCallback(
    (c: Contact) => {
      setSelected(c);
      openDetail();
    },
    [openDetail]
  );

  const copyToClipboard = useCallback(async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopyalandı`);
    } catch {
      toast.error("Kopyalama başarısız");
    }
  }, []);

  const onCopyEmail = useCallback(
    (email: string) => copyToClipboard(email, "E-posta"),
    [copyToClipboard]
  );
  const onCopyPhone = useCallback(
    (phone: string) => copyToClipboard(phone, "Telefon"),
    [copyToClipboard]
  );

  const totalPages = resp?.pageCount ?? 1;

  return (
    <div className="p-3">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-3">
        <h1 className="text-xl font-bold">Mesajlar</h1>
        <Text size="sm" c="dimmed">
          {resp
            ? `${resp.totalCount.toLocaleString("tr-TR")} kayıt • Sayfa ${
                pageIndex + 1
              }/${totalPages} • Bu sayfada ${resp.items.length}`
            : "Yükleniyor..."}
        </Text>
      </div>

      <Card withBorder shadow="xs" radius="md">
        <ScrollArea style={{ maxHeight: 620 }}>
          <Table striped highlightOnHover stickyHeader>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Durum</Table.Th>
                <Table.Th>Gönderen</Table.Th>
                <Table.Th>Konu</Table.Th>
                <Table.Th>Mesaj</Table.Th>
                <Table.Th className="text-right">İşlemler</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading && (!resp || resp.items.length === 0) && (
                <>
                  {Array.from({ length: 10 }).map((_, i) => (
                    <Table.Tr key={`sk-${i}`}>
                      <Table.Td colSpan={5}>
                        <div className="h-6 w-full bg-gray-100 animate-pulse rounded" />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </>
              )}

              {!loading && resp?.items.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" ta="center">
                      Gösterilecek mesaj bulunamadı.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}

              {resp?.items.map((item) => (
                <ContactRow
                  key={item.code}
                  item={item}
                  onOpen={openDetails}
                  onCopyEmail={onCopyEmail}
                  onCopyPhone={onCopyPhone}
                  markAsRead={markAsRead}
                />
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>

        <div className="flex items-center justify-center mt-4">
          <Pagination
            value={pageIndex + 1}
            onChange={(p) => setPageIndex(p - 1)} // Mantine 1-based
            total={totalPages}
            withEdges
          />
        </div>
      </Card>

      <Modal
        opened={detailOpened}
        onClose={closeDetail}
        title="Mesaj Detayı"
        size="lg"
        centered
      >
        {selected ? (
          <div className="space-y-2">
            <Group justify="space-between">
              <Text fw={600}>{selected.fullName}</Text>
              {selected.read ? (
                <Badge color="green" variant="light">
                  Okundu
                </Badge>
              ) : (
                <Badge color="red" variant="light">
                  Okunmadı
                </Badge>
              )}
            </Group>
            <Group gap="xs" wrap="wrap">
              <Text size="sm" c="dimmed">
                E-posta:
              </Text>
              <a
                href={`mailto:${selected.email}`}
                className="text-blue-600 hover:underline"
              >
                {selected.email}
              </a>
              <Text size="sm" c="dimmed">
                • Telefon:
              </Text>
              <a
                href={`tel:${selected.phone}`}
                className="text-blue-600 hover:underline"
              >
                {selected.phone}
              </a>
            </Group>
            <Text size="sm" c="dimmed">
              Konu: {selected.subject ?? "—"}
            </Text>
            <Card withBorder padding="md" radius="md">
              <Text style={{ whiteSpace: "pre-wrap" }}>{selected.message}</Text>
            </Card>
            <Group justify="flex-end" gap="xs">
              <Button
                variant="light"
                onClick={() => onCopyEmail(selected.email)}
              >
                E-posta kopyala
              </Button>
              <Button
                variant="light"
                onClick={() => onCopyPhone(selected.phone)}
              >
                Telefon kopyala
              </Button>
            </Group>
          </div>
        ) : (
          <Text c="dimmed">Seçili mesaj yok</Text>
        )}
      </Modal>
    </div>
  );
};

export default ContactMessages;
